import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    updateProfile,
    sendPasswordResetEmail,
    GoogleAuthProvider,
    signInWithPopup,
} from 'firebase/auth';
import { 
    getFirestore,
    doc,
    getDoc,
    writeBatch, 
    serverTimestamp 
} from 'firebase/firestore';
import { auth } from '../firebase';

const db = getFirestore();
const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);

  // Email/Password Signup
  async function signup(email, password, username) {
    const isValid = /^(?=[a-zA-Z0-9._]{3,20}$)(?!.*[_.]{2})[^_.].*[^_.]$/.test(username);
    if (!isValid) {
        throw new Error("Username must be 3-20 characters long and can only contain letters, numbers, an underscore, or a period.");
    }
    const usernameRef = doc(db, "usernames", username.toLowerCase());
    const usernameSnap = await getDoc(usernameRef);
    if (usernameSnap.exists()) {
      throw new Error("That username is already taken.");
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    await updateProfile(user, { displayName: username });
    
    const batch = writeBatch(db);
    const userDocRef = doc(db, "users", user.uid);
    batch.set(userDocRef, {
      username: username,
      email: user.email,
      createdAt: serverTimestamp(),
      uid: user.uid,
    });
    batch.set(usernameRef, { uid: user.uid });
    await batch.commit();
    
    // Manually update the currentUser state to reflect the new displayName
    setCurrentUser({
        ...user,
        displayName: username
    });
    
    return userCredential;
  }
  
  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const logout = () => signOut(auth);
  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  async function loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Create Firestore user doc on first Google sign-in
    const userDocRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userDocRef);
    if (!userSnap.exists()) {
      // Derive a username from the Google display name
      const baseUsername = (user.displayName || 'user').replace(/\s+/g, '').toLowerCase().slice(0, 20);
      const usernameRef = doc(db, 'usernames', baseUsername);
      const usernameSnap = await getDoc(usernameRef);

      // Append uid suffix if username is taken
      const finalUsername = usernameSnap.exists() ? `${baseUsername}${user.uid.slice(0, 5)}` : baseUsername;

      const batch = writeBatch(db);
      batch.set(userDocRef, {
        username: finalUsername,
        email: user.email,
        createdAt: serverTimestamp(),
        uid: user.uid,
        photoURL: user.photoURL || null,
      });
      batch.set(doc(db, 'usernames', finalUsername), { uid: user.uid });
      await batch.commit();
    }

    return result;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
      setInitializing(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    resetPassword,
    loginWithGoogle,
    initializing,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}